import React, { useEffect, useState } from "react";
import { Check, ArrowRight, ChevronDown } from "lucide-react";
import { PLAN_ORDER, PLAN_META, PLAN_QUOTAS, planHighlights, loadPlanQuotas } from "@/lib/plans";
import { PlanCompare } from "@/components/shared/PlanCompare";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const formatPrice = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num) || num === 0) return "0";
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

// Plans, prices and bullets all come from the same catalog as the in-app plan
// picker (lib/plans.js): live quotas from /api/ai/plans, live prices from
// /api/payments/tiers, so this copy can't drift from what's enforced.
export function PricingSection({ isLight, onStartFree, onChoosePlan }) {
  const [quotas, setQuotas] = useState(PLAN_QUOTAS);
  const [tiers, setTiers] = useState(null);
  const [showCompare, setShowCompare] = useState(false);

  useEffect(() => {
    loadPlanQuotas(API).then(setQuotas);
    fetch(`${API}/payments/tiers`).then((r) => r.json()).then((d) => setTiers(d.tiers)).catch(() => {});
  }, []);

  const muted = isLight ? "text-[#6B8578]" : "text-[#9FB8AC]";
  const heading = isLight ? "text-[#3D9970]" : "text-white";
  const card = isLight ? "border-[#DCEBE3] bg-white" : "border-white/10 bg-[#16332A]";

  return (
    <section id="pricing-section" data-testid="landing-pricing" className="scroll-mt-20 px-5 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A67C52]">Pricing</p>
          <h2 className={`mt-3 font-['Manrope'] text-3xl font-bold tracking-tight md:text-[2.75rem] ${heading}`}>Simple, transparent pricing</h2>
          <p className={`mt-4 text-base leading-7 md:text-lg ${muted}`}>Start free and upgrade when you&rsquo;re ready to apply. Monthly billing, cancel anytime.</p>
        </div>

        <div className="mt-14 grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PLAN_ORDER.map((key) => {
            const meta = PLAN_META[key];
            const price = key === "free" ? 0 : (tiers?.[key]?.final_amount ?? tiers?.[key]?.amount ?? meta.price);
            const listPrice = tiers?.[key]?.amount;
            const discounted = key !== "free" && listPrice != null && Number(price) < Number(listPrice);
            const best = meta.recommended;
            return (
              <article
                key={key}
                data-testid={`landing-plan-${key}`}
                className={`relative flex flex-col rounded-2xl border p-6 transition-shadow ${card} ${
                  best ? "border-[#A67C52] shadow-[0_24px_60px_-28px_rgba(166,124,82,0.6)] ring-1 ring-[#A67C52] lg:-my-3 lg:py-9" : "hover:shadow-[0_16px_40px_-28px_rgba(15,33,27,0.5)]"
                }`}
              >
                {best && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-[#A67C52] px-3 py-1 text-[10px] font-black uppercase tracking-wider text-white">
                    Most popular
                  </span>
                )}
                <h3 className={`text-lg font-bold ${best ? "text-[#A67C52]" : heading}`}>{meta.name}</h3>
                <p className={`mt-1 text-sm ${muted}`}>{meta.tagline}</p>

                <p className={`mt-6 flex items-baseline gap-1 ${isLight ? "text-[#0F211B]" : "text-white"}`}>
                  {discounted && <s className={`mr-1 text-lg font-semibold ${muted}`}>${formatPrice(listPrice)}</s>}
                  <span className="font-['Manrope'] text-4xl font-extrabold tracking-tight">${formatPrice(price)}</span>
                  <span className={`text-sm font-medium ${muted}`}>{key === "free" ? "forever" : "/ month"}</span>
                </p>

                <button
                  type="button"
                  data-testid={`landing-plan-cta-${key}`}
                  onClick={() => (key === "free" ? onStartFree?.() : onChoosePlan?.(key))}
                  className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] focus-visible:ring-offset-2 ${
                    best
                      ? "bg-[#A67C52] text-white shadow-[0_10px_28px_-12px_rgba(166,124,82,0.55)] hover:bg-[#8A6640]"
                      : isLight
                      ? "border border-black/15 text-[#3D9970] hover:border-black/40"
                      : "border border-white/20 text-white hover:border-[#A67C52]"
                  }`}
                >
                  {key === "free" ? "Start free" : `Choose ${meta.name}`} <ArrowRight className="size-4" />
                </button>

                <div className={`my-6 h-px ${isLight ? "bg-[#DCEBE3]" : "bg-white/10"}`} />

                <ul className="flex-1 space-y-3">
                  {planHighlights(key, quotas).map((f) => (
                    <li key={f} className={`flex gap-2.5 text-sm leading-5 ${isLight ? "text-[#1F3A2F]" : "text-[#E3EFE9]"}`}>
                      <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-[#3D9970]/15 text-[#3D9970]">
                        <Check className="size-2.5" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <p className={`mt-8 text-center text-xs ${muted}`}>Prices in USD. Every plan has monthly AI limits, so usage stays fair for everyone.</p>

        <div className="mt-6 text-center">
          <button
            type="button"
            data-testid="landing-compare-toggle"
            aria-expanded={showCompare}
            onClick={() => setShowCompare((v) => !v)}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-[#A67C52] hover:underline"
          >
            {showCompare ? "Hide full comparison" : "Compare every feature"}
            <ChevronDown className={`size-4 transition-transform ${showCompare ? "rotate-180" : ""}`} />
          </button>
        </div>
        {showCompare && (
          <div className={`mt-6 overflow-x-auto rounded-2xl border p-5 ${card}`}>
            <PlanCompare quotas={quotas} isLight={isLight} />
          </div>
        )}
      </div>
    </section>
  );
}
